/**
 * Template 2: Urgency Flash Sale
 *
 * High-energy countdown with scarcity indicators.
 * Target: Flash sales, limited-time offers, FOMO-driven campaigns
 *
 * Two-Phase Animation Timeline (15 seconds @ 60fps = 900 frames):
 *
 * PHASE 1: Flash Sale Showcase (0-7.5s / frames 0-225)
 * - 0.0-0.3s (0-9):     Flash burst intro
 * - 0.3-1.0s (9-30):    "FLASH SALE" banner slam-in
 * - 1.0-2.0s (30-60):   Product image dramatic entrance
 * - 2.0-2.5s (60-75):   Discount badge rotation in
 * - 2.5-3.5s (75-105):  Price reveal
 * - 3.5-5.0s (105-150): Countdown timer & stock warning
 * - 5.0-7.0s (150-210): Discount code reveal
 * - 7.0-7.5s (210-225): Fade out transition
 *
 * PHASE 2: Caption Scene (7.5-15s / frames 225-450)
 * - 7.5-8.0s (225-240):  Scene fade in
 * - 8.0-12.0s (240-360): Caption typewriter
 * - 12.0-15s (360-450):  Hold with swipe-up indicator
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import type { UrgencyFlashSaleProps, VisualEffectsConfig } from '../types';
import { DEFAULT_VISUAL_EFFECTS, FONT_ID_TO_FAMILY } from '../types';
import { ProductImage } from '../components/ProductImage';
import { AnimatedText } from '../components/AnimatedText';
import { AnimatedPrice } from '../components/AnimatedPrice';
import { CountdownTimer } from '../components/CountdownTimer';
import { CaptionScene } from '../components/CaptionScene';
import { TemplateBackground } from '../components/TemplateBackground';
import { SparkleCluster } from '../components/Sparkles';
import { ShimmerEffect } from '../components/ShimmerEffect';
import { DiscountBadge } from '../components/DiscountBadge';

export const UrgencyFlashSale: React.FC<UrgencyFlashSaleProps> = ({
  productTitle,
  productImage,
  originalPrice,
  salePrice,
  discountPercent,
  discountCode,
  discountLabel,
  countdownSeconds,  // Optional - no default (prevents fake urgency)
  stockCount,        // Optional - no default (prevents fake scarcity)
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  effects: effectsOverride,
  backgroundType = 'aurora',
  backgroundPalette = 'lava',
  textAnimation = 'slide_up',
  language,
  showPriceDisclaimer = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Merge user effects with defaults
  const effects: VisualEffectsConfig = {
    ...DEFAULT_VISUAL_EFFECTS,
    ...effectsOverride,
  };

  // Resolve font family from font ID (for Phase 1 text components)
  const fontFamily = FONT_ID_TO_FAMILY[captionFont] || 'Inter, sans-serif';

  // Phase transition point
  const PHASE_TRANSITION = fps * 7.5; // Frame 225

  // Phase 1: Timeline markers (compressed to 7.5s)
  const FLASH_END = fps * 0.3;
  const BANNER_START = fps * 0.3;
  const PRODUCT_START = fps * 1;
  const BADGE_START = fps * 2;
  const PRICE_START = fps * 2.5;
  const COUNTDOWN_START = fps * 3.5;
  const STOCK_START = fps * 4.5;
  const CODE_START = fps * 5.5;

  // Phase 1 fade out
  const phase1Opacity = interpolate(
    frame,
    [PHASE_TRANSITION - fps * 0.5, PHASE_TRANSITION],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Flash burst effect - reduced intensity for accessibility
  // (original was [1, 0] which could be jarring for some users)
  const flashOpacity = interpolate(
    frame,
    [0, FLASH_END],
    [0.3, 0],
    { extrapolateRight: 'clamp' }
  );

  // Banner slam-in animation
  const bannerScale = spring({
    frame: frame - BANNER_START,
    fps,
    from: 2,
    to: 1,
    durationInFrames: fps * 0.4,
    config: { stiffness: 300, damping: 20 },
  });
  const bannerOpacity = interpolate(
    frame,
    [BANNER_START, BANNER_START + fps * 0.2],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Discount badge animation
  const badgeRotation = spring({
    frame: frame - BADGE_START,
    fps,
    from: 45,
    to: 12,
    durationInFrames: fps * 0.5,
    config: { stiffness: 200, damping: 15 },
  });
  const badgeScale = spring({
    frame: frame - BADGE_START,
    fps,
    from: 0,
    to: 1,
    durationInFrames: fps * 0.4,
  });

  // Stock warning animation
  const stockOpacity = interpolate(
    frame,
    [STOCK_START, STOCK_START + fps * 0.3],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
  const stockPulse = frame > STOCK_START
    ? 1 + Math.sin((frame - STOCK_START) * 0.2) * 0.05
    : 1;

  // Determine if we're in Phase 1 or Phase 2
  const isPhase1 = frame < PHASE_TRANSITION;
  const isPhase2 = frame >= PHASE_TRANSITION - fps * 0.5;

  // Flash sale uses warm fire colors
  const brandColor = '#8B0000'; // Dark red base
  const accentColor = '#FF6B35'; // Orange accent

  // Phase 1 content (shared between all background types)
  const phase1Content = (
    <>
      <AbsoluteFill
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 50px',
          opacity: phase1Opacity,
        }}
      >
        {/* Flash burst overlay */}
        <AbsoluteFill
          style={{
            backgroundColor: '#ffffff',
            opacity: flashOpacity,
            pointerEvents: 'none',
          }}
        />

        {/* FLASH SALE Banner (uses selected text animation) */}
        <div
          style={{
            backgroundColor: '#000000',
            padding: '12px 50px',
            marginBottom: 30,
            opacity: bannerOpacity,
            transform: `scale(${bannerScale})`,
          }}
        >
          <AnimatedText
            text="FLASH SALE"
            animationType={textAnimation}
            startFrame={BANNER_START}
            color="#FFD700"
            fontSize={44}
            fontWeight={900}
            fontFamily={fontFamily}
            textAlign="center"
            wordDelay={4}
          />
        </div>

        {/* Product Image with Discount Badge */}
        <div style={{ position: 'relative', marginBottom: 30 }}>
          <ProductImage
            src={productImage}
            alt={productTitle}
            startFrame={PRODUCT_START}
            width={500}
            height={500}
            borderRadius={20}
            shadow="0 25px 60px rgba(0,0,0,0.4)"
            border="3px solid rgba(255,255,255,0.3)"
            entranceAnimation="slideUp"
            enableFloat={true}
            floatAmplitude={3}
          />

          {/* Discount Badge */}
          {discountPercent > 0 && (
            <div
              style={{
                position: 'absolute',
                top: -15,
                right: -15,
                backgroundColor: '#FFD700',
                padding: '18px 22px',
                borderRadius: '50%',
                transform: `rotate(${badgeRotation}deg) scale(${badgeScale})`,
                boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
              }}
            >
              <span
                style={{
                  color: '#000000',
                  fontSize: 34,
                  fontWeight: 900,
                  fontFamily,
                }}
              >
                -{discountPercent}%
              </span>
            </div>
          )}

          {/* Sparkles around the product (fire colors) */}
          {effects.sparkles && (
            <>
              <SparkleCluster
                centerX={50}
                centerY={50}
                radius={45}
                count={16}
                color="#FFD700"
                startFrame={PRODUCT_START + fps * 0.6}
                seed={11111}
              />
              {/* Additional sparkles near the discount badge */}
              <SparkleCluster
                centerX={88}
                centerY={12}
                radius={12}
                count={8}
                color="#FF6B35"
                startFrame={BADGE_START + fps * 0.3}
                seed={22222}
              />
            </>
          )}
        </div>

        {/* Product Title (uses selected text animation) */}
        <div
          style={{
            marginBottom: 16,
            maxWidth: 700,
            textShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}
        >
          <AnimatedText
            text={productTitle}
            animationType={textAnimation}
            startFrame={PRODUCT_START + fps * 0.5}
            color={captionColor}
            fontSize={42}
            fontWeight={700}
            fontFamily={fontFamily}
            textAlign="center"
            wordDelay={4}
          />
        </div>

        {/* Prices with Shimmer Effect */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <AnimatedPrice
            originalPrice={originalPrice}
            salePrice={salePrice}
            startFrame={PRICE_START}
            salePriceColor={captionColor}
            originalPriceColor="rgba(255,255,255,0.5)"
            salePriceFontSize={56}
            originalPriceFontSize={32}
            direction="row"
            gap={20}
            showPriceDisclaimer={showPriceDisclaimer}
            language={language}
          />

          {/* Shimmer effect over the price */}
          {effects.priceShimmer && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: -20,
                right: -20,
                bottom: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
            >
              <ShimmerEffect
                width={80}
                color="rgba(255, 215, 0, 0.4)"
                delay={fps * 0.6}
                duration={30}
                repeatCount={3}
                repeatGap={fps * 1.5}
                angle={-20}
                intensity={0.8}
                startFrame={PRICE_START}
                blur={10}
              />
            </div>
          )}
        </div>

        {/* Countdown Timer - only shown if countdownSeconds is provided (prevents fake urgency) */}
        {countdownSeconds && countdownSeconds > 0 && (
          <div style={{ marginBottom: 20 }}>
            <CountdownTimer
              totalSeconds={countdownSeconds}
              startFrame={COUNTDOWN_START}
              showHours={true}
              boxBackgroundColor="rgba(0,0,0,0.4)"
              textColor="#ffffff"
              labelColor="rgba(255,255,255,0.7)"
              fontSize={44}
              enablePulse={true}
            />
          </div>
        )}

        {/* Stock Warning and Discount Code */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 18,
          }}
        >
          {/* Stock Warning - only shown if stockCount is provided (prevents fake scarcity) */}
          {stockCount && stockCount > 0 && (
            <div
              style={{
                opacity: stockOpacity,
                transform: `scale(${stockPulse})`,
                textShadow: '0 0 20px rgba(255, 215, 0, 0.6)',
              }}
            >
              <AnimatedText
                text={`Only ${stockCount} left!`}
                animationType={textAnimation}
                startFrame={STOCK_START}
                color="#FFD700"
                fontSize={38}
                fontWeight={700}
                fontFamily={fontFamily}
                textAlign="center"
                wordDelay={3}
              />
            </div>
          )}

          {/* Discount Code Badge */}
          <DiscountBadge
            discountCode={discountCode}
            discountLabel={discountLabel}
            variant="gold"
            showFrom={CODE_START}
            fontFamily={fontFamily}
            textAnimation={textAnimation}
          />
        </div>
      </AbsoluteFill>
    </>
  );

  // Phase 2 content (caption overlay - no background, transparent)
  const phase2Content = captionText ? (
    <CaptionScene
      captionText={captionText}
      captionColor={captionColor}
      captionFont={captionFont}
      textAnimation={textAnimation}
      startFrame={PHASE_TRANSITION}
      gradientPreset="none"
      showSwipeUp={true}
      language={language}
    />
  ) : null;

  // Combined content for both phases (layered on top of background)
  const combinedContent = (
    <>
      {isPhase1 && phase1Content}
      {isPhase2 && phase2Content}
    </>
  );

  return (
    <TemplateBackground
      backgroundType={backgroundType}
      backgroundPalette={backgroundPalette}
      colorTheme={{
        brandColor,
        accentColor,
        fallbackGradientEnd: '#1a0000',
      }}
      effects={effects}
      fps={fps}
      rippleConfig={{ startFrame: PRODUCT_START }}
    >
      {combinedContent}
    </TemplateBackground>
  );
};
